import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Lock, AlertCircle, Loader2 } from 'lucide-react';

/**
 * OwnerBootstrapLoginPage — lightweight password fallback for pre-approved
 * owner accounts. POSTs to /api/auth/owner-bootstrap/login and stores the
 * session_token in localStorage (matching App.js's AUTH_TOKEN_KEY).
 *
 * Route: /owner-login
 */
const AUTH_TOKEN_KEY = 'orka_session_token';

export default function OwnerBootstrapLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/owner-bootstrap/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      if (data.session_token) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.session_token);
      }
      // After the localStorage write, App.js's fetch interceptor will pick up the
      // new token. Reload to /owner so the auth-provider picks up the session.
      window.location.replace('/owner/overview');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen orka-surface-page flex items-center justify-center px-6 py-12" data-testid="owner-bootstrap-login">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Crown className="w-7 h-7 text-amber-500" />
          <span className="text-xl font-semibold tracking-tight orka-text-primary">Orka Owner</span>
        </div>
        <div className="orka-surface-raised border orka-border-subtle rounded-3xl p-8 shadow-[0_14px_40px_rgba(0,0,0,0.08)]">
          <h1 className="text-2xl font-bold orka-text-primary">Owner bootstrap sign-in</h1>
          <p className="mt-1.5 text-sm orka-text-secondary">
            Password fallback for pre-approved owner emails. Google SSO is recommended for day-to-day use.
          </p>
          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-wider orka-text-tertiary font-semibold">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 px-3 rounded-lg border orka-border-default orka-surface-base orka-text-primary orka-focus-ring"
                placeholder="you@example.com"
                autoComplete="email"
                data-testid="owner-bootstrap-email"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-wider orka-text-tertiary font-semibold">Password</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 orka-text-tertiary" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-9 pr-3 rounded-lg border orka-border-default orka-surface-base orka-text-primary orka-focus-ring"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="owner-bootstrap-password"
                />
              </div>
            </label>
            {error && (
              <div
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-rose-700 bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800"
                data-testid="owner-bootstrap-error"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="h-11 mt-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 orka-transition-colors orka-focus-ring"
              data-testid="owner-bootstrap-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <div className="mt-6 flex items-center justify-between gap-3 text-xs orka-text-tertiary">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="hover:orka-text-primary orka-focus-ring rounded-sm"
            >
              ← Back to Orka
            </button>
            <span>Preferred: <a href="/" className="underline underline-offset-2">Google SSO</a></span>
          </div>
        </div>
      </div>
    </div>
  );
}
